/**
 * Gift permalink realtime helpers — three small `(prev, raw) => next` mappers
 * that the `useGetGiftsPermalink` realtime block declares against the
 * `morfi.gift.{giftConfig}.event.v0` subject.
 *
 * The `GiftEvent` payload (proto `morfi.v1.GiftEvent`) carries:
 *   - `kind: GiftEventKind` (1=CREATED 2=CLAIMED)
 *   - `ts: number` (unix seconds)
 *   - `claimer?: string` (base58 wallet that claimed; CLAIMED only)
 *   - `giftConfig: string` — on-chain `GiftDeposit` account pubkey, IS
 *     the deposit pubkey. Used to match the contributor row to flip on
 *     CLAIMED. Chain-derivable — no App DB lookup needed.
 *
 * All three helpers are immutable and idempotent — they return the exact
 * `prev` reference when no change applies, keeping TanStack's
 * structural-sharing short-circuits intact.
 */

/** Numeric `morfi.v1.GiftEventKind` (see proto). */
const KIND_CREATED = 1;
const KIND_CLAIMED = 2;

function coerceKind(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (/^\d+$/.test(trimmed)) return Number(trimmed);
    switch (trimmed) {
      case 'CREATED':
      case 'GIFT_EVENT_KIND_CREATED':
        return KIND_CREATED;
      case 'CLAIMED':
      case 'GIFT_EVENT_KIND_CLAIMED':
        return KIND_CLAIMED;
      default:
        return 0;
    }
  }
  return 0;
}

/**
 * On a CLAIMED event, set the permalink row's `status` to `'claimed'`.
 * CREATED is a no-op — the row may not exist yet on a fresh permalink
 * (the cache row is hydrated by the REST GET; a CREATED event arriving
 * before the row is hydrated finds an undefined cache key and the SDK
 * dispatch path no-ops).
 */
export function mapGiftEventKindToPermalinkStatus<T extends { readonly status?: string }>(
  prev: T,
  raw: Record<string, unknown>,
): T {
  if (coerceKind(raw.kind) !== KIND_CLAIMED) return prev;
  if (prev.status === 'claimed') return prev;
  return { ...prev, status: 'claimed' };
}

/**
 * On a CLAIMED event, write the event's `ts` (unix seconds) into the
 * permalink row's `claimedAt` ISO string.
 */
export function setPermalinkClaimedAtIfClaimed<T extends { readonly claimedAt?: string }>(
  prev: T,
  raw: Record<string, unknown>,
): T {
  if (coerceKind(raw.kind) !== KIND_CLAIMED) return prev;
  const ts = raw.ts;
  if (typeof ts !== 'number' || !Number.isFinite(ts)) return prev;
  const iso = new Date(ts * 1000).toISOString();
  if (prev.claimedAt === iso) return prev;
  return { ...prev, claimedAt: iso };
}

/** Shape of one contributor row inside the permalink response. */
interface ContributorRowLike {
  readonly depositPubkey?: string;
  readonly claimed?: boolean;
}

/**
 * On a CLAIMED event, flip the matching contributor row's `claimed=true`
 * inside the permalink's `contributors[]` array. The match is by
 * `depositPubkey === raw.giftConfig` — the chain event's `gift_config`
 * IS the on-chain `GiftDeposit` account pubkey, which is what each
 * contributor row carries as `depositPubkey`.
 *
 * When the contributors array is undefined (caller didn't pass
 * `?includeContributors=true`) the helper bails.
 */
export function markContributorClaimedOnEvent<
  C extends ContributorRowLike,
  T extends { readonly contributors?: readonly C[] },
>(prev: T, raw: Record<string, unknown>): T {
  if (coerceKind(raw.kind) !== KIND_CLAIMED) return prev;
  const target = typeof raw.giftConfig === 'string' ? raw.giftConfig : '';
  if (target.length === 0) return prev;
  const contributors = prev.contributors;
  if (!Array.isArray(contributors) || contributors.length === 0) return prev;
  let changed = false;
  const next = contributors.map((c) => {
    if (c.depositPubkey === target && c.claimed !== true) {
      changed = true;
      return { ...c, claimed: true };
    }
    return c;
  });
  if (!changed) return prev;
  return { ...prev, contributors: next };
}
